import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  Database,
  Languages,
  Palette,
  FileDown,
  Cloud,
  type LucideIcon,
} from 'lucide-react';

type Feature = {
  icon: LucideIcon;
  titleKey: string;
  descKey: string;
  iconClass: string;
  borderHover: string;
};

const FEATURES: Feature[] = [
  {
    icon: ShieldCheck,
    titleKey: 'home.features.privacyTitle',
    descKey: 'home.features.privacyDesc',
    iconClass: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
    borderHover: 'hover:border-emerald-300 dark:hover:border-emerald-500/40',
  },
  {
    icon: Database,
    titleKey: 'home.features.offlineTitle',
    descKey: 'home.features.offlineDesc',
    iconClass: 'bg-sky-500/15 text-sky-600 dark:text-sky-300',
    borderHover: 'hover:border-sky-300 dark:hover:border-sky-500/40',
  },
  {
    icon: Languages,
    titleKey: 'home.features.i18nTitle',
    descKey: 'home.features.i18nDesc',
    iconClass: 'bg-violet-500/15 text-violet-600 dark:text-violet-300',
    borderHover: 'hover:border-violet-300 dark:hover:border-violet-500/40',
  },
  {
    icon: Palette,
    titleKey: 'home.features.templatesTitle',
    descKey: 'home.features.templatesDesc',
    iconClass: 'bg-amber-500/15 text-amber-600 dark:text-amber-300',
    borderHover: 'hover:border-amber-300 dark:hover:border-amber-500/40',
  },
  {
    icon: FileDown,
    titleKey: 'home.features.exportTitle',
    descKey: 'home.features.exportDesc',
    iconClass: 'bg-rose-500/15 text-rose-600 dark:text-rose-300',
    borderHover: 'hover:border-rose-300 dark:hover:border-rose-500/40',
  },
  {
    icon: Cloud,
    titleKey: 'home.features.syncTitle',
    descKey: 'home.features.syncDesc',
    iconClass: 'bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300',
    borderHover: 'hover:border-fuchsia-300 dark:hover:border-fuchsia-500/40',
  },
];

export function FeaturesGrid() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden border-t border-border bg-muted/30 dark:bg-muted/10">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-0 size-[420px] rounded-full bg-sky-300/20 blur-[120px] dark:bg-sky-500/10" />
        <div className="absolute bottom-0 right-0 size-[420px] rounded-full bg-fuchsia-300/20 blur-[120px] dark:bg-fuchsia-500/10" />
      </div>
      <div className="relative mx-auto max-w-6xl px-4 py-16 md:py-24">
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <p className="text-sm font-semibold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            {t('home.features.eyebrow')}
          </p>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
            {t('home.features.title')}
          </h2>
          <p className="mt-3 text-base text-muted-foreground md:text-lg">
            {t('home.features.subtitle')}
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.titleKey}
                className={`group rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${f.borderHover}`}
              >
                <div
                  className={`inline-flex size-11 items-center justify-center rounded-lg ${f.iconClass}`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{t(f.titleKey)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {t(f.descKey)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
